import { Icon } from '@mdvip/shared/components/blocks/Icon';
import type { CSSProperties } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

/**
 * App-root toast mount, backed by sonner. Mount once near the app root (done in both apps'
 * App.tsx). Trigger toasts via the `useToast` hook, not this component directly.
 *
 * Design tokens are stored as full OKLCH color values (see packages/shared/src/styles/tokens.css),
 * so these CSS vars are consumed directly — no hsl()/color-function wrapper needed.
 *
 * Three deliberate departures from sonner's defaults, all to match the design system's toast:
 *
 * 1. `icons` replaces sonner's bundled filled SVGs with the shared Font Awesome `Icon`, whose
 *    regular face is the outlined mark the design uses. Colour still comes from the per-type rules
 *    in styles/globals.css.
 * 2. `items-start` on the toast, because sonner centres the icon against the whole toast, which
 *    drops it alongside the second line of a two-line description instead of the title.
 * 3. The close button is styled and positioned for the design, but left off by default: callers
 *    opt in per toast via `toast({ closeButton: true })`. Sonner would otherwise park it outside
 *    the top-left corner dressed as a bordered filled circle.
 */
function Toaster(props: ToasterProps) {
  return (
    <Sonner
      className="toaster group"
      position="bottom-right"
      icons={{
        success: <Icon name="circleCheck" />,
        error: <Icon name="circleXmark" />,
        warning: <Icon name="alertTriangle" />,
        info: <Icon name="info" />,
        close: <Icon name="x" size="xs" />,
      }}
      toastOptions={{
        classNames: {
          toast: 'group/toast items-start!',
          // The close button is absolutely positioned in the same top-right corner the action
          // button hugs, so the action reserves room for it, giving the design's
          // action-then-close order. Keyed off the close button actually being in the DOM, so a
          // toast without one leaves the action where sonner puts it.
          actionButton: 'bg-primary! text-primary-foreground! group-has-[[data-close-button]]/toast:mr-6!',
          // Sonner ships the close button as a bordered, filled circle. The design has a bare
          // muted glyph that only picks up a background on hover.
          closeButton:
            'border-0! bg-transparent! rounded-md! text-muted-foreground! hover:bg-muted! hover:text-foreground!',
        },
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
          // Sonner's LTR default is `start: 0` with a translate that hangs the button off the
          // top-left corner. The design puts it inside the top-right.
          '--toast-close-button-start': 'unset',
          '--toast-close-button-end': '0',
          '--toast-close-button-transform': 'translate(-35%, 35%)',
        } as CSSProperties
      }
      {...props}
    />
  );
}

export { Toaster };
